import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Snackbar, Alert as MuiAlert, Box } from "@mui/material";
import { useDarkMode, useDevMode } from "../../contexts/ThemeContext";
import { useWorkspace } from "../../contexts/WorkspaceContext";
import { useTranslation } from "react-i18next";
import { faArrowUp } from "@fortawesome/free-solid-svg-icons";
import CounterCards from "../../components/DonateMaterials/CounterCards";
import MaterialsAccordion from "../../components/DonateMaterials/MaterialsAccordion";
import SaveChangesModal from "../../components/Modals/SaveChangesModal";
import Alert from "../../components/ui/Alert";
import ActionButton from "../../components/ui/ActionButton";
import LoadingSpinner from "../../components/ui/LoadingSpinner";
import LastUpdatedBadge from "../../components/ui/LastUpdatedBadge";
import { useDonateMaterialsData } from "../../hooks/useDonateMaterialsData";
import { useDevModeDonateMaterialsSetup } from "../../hooks/useDevModeDonateMaterialsSetup";
import { usePermissionGuard } from "../../hooks/usePermissionGuard";
import { exportToCSV } from "../../utils/exportUtils";
import config from "../../config";
import "../../style/Card.css";
import "../../style/DonateMaterials.css";

function DonateMaterials() {
  // theme and translation hooks
  const { darkMode } = useDarkMode();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setWorkspaceListing } = useWorkspace();
  const { registerDevAction, clearDevActions } = useDevMode();
  // TODO: wire up when delivery UI is implemented
  // const [deliveryMethod, setDeliveryMethod] = useState("pickup");
  // const [transportMode, setTransportMode] = useState("");
  // const [instructions, setInstructions] = useState("");

  // permission guard - redirects to /no-permission if user lacks access
  const { hasPermission, isChecking } = usePermissionGuard("donor", "donate_materials");

  // ref to access MaterialsTable imperative methods
  const materialsTableRef = useRef(null);
  const materialsAccordionRef = useRef(null);

  // Timer refs for cleanup
  const refreshTimerRef = useRef(null);
  const deepLinkTimerRef = useRef(null);
  const clearCellsTimerRef = useRef(null);

  // URL params for deep linking to specific listing
  const [searchParams, setSearchParams] = useSearchParams();
  const listingIdParam = searchParams.get("listing");

  // State for showing all listings
  const [showAllListings, setShowAllListings] = useState(
    config.isDemoMode ? config.devShowAllListings : false
  );

  // UI state management
  const [initialLoading, setInitialLoading] = useState(true);
  const [isVisible, setIsVisible] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [selectedListing, setSelectedListing] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [justUpdated, setJustUpdated] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isDetailPanelOpen, setIsDetailPanelOpen] = useState(false);
  const [displayedRowId, setDisplayedRowId] = useState(null);
  const [scrollButtonUsed, setScrollButtonUsed] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState(new Set());
  const [materialFilter, setMaterialFilter] = useState("all");
  const [sortBy, setSortBy] = useState("newest");
  const [viewMode, setViewMode] = useState(() => {
    return localStorage.getItem("donate_view_mode") || "table";
  });
  const justUpdatedTimerRef = useRef(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Setup dev mode toggle
  const handleToggleShowAll = useCallback((newValue) => {
    console.log(`Toggling showAllListings to: ${newValue}`);
    setShowAllListings(newValue);
  }, []);

  useDevModeDonateMaterialsSetup(
    registerDevAction,
    clearDevActions,
    handleToggleShowAll
  );

  // data management hook
  const {
    loading,
    counters,
    listingsData,
    hasUnsavedChanges,
    errorMessage,
    lastUpdated,
    setHasUnsavedChanges,
    handleDataUpdate,
    performRefresh,
    handleAddListing,
    handleBulkAddListings,
    handleDeleteListings,
    handleToggleVisibility,
  } = useDonateMaterialsData(materialsTableRef, showAllListings);

  // add 1500ms delay on initial load
  useEffect(() => {
    const timer = setTimeout(() => {
      setInitialLoading(false);
    }, 1500);

    return () => clearTimeout(timer);
  }, []);

  // Trigger fade-in animation after loading completes
  useEffect(() => {
    if (!initialLoading && !loading) {
      const timer = setTimeout(() => setIsVisible(true), 50);
      return () => clearTimeout(timer);
    }
  }, [initialLoading, loading]);

  // Cleanup all timers on unmount
  useEffect(() => {
    return () => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
      if (deepLinkTimerRef.current) clearTimeout(deepLinkTimerRef.current);
      if (clearCellsTimerRef.current) clearTimeout(clearCellsTimerRef.current);
      if (justUpdatedTimerRef.current) clearTimeout(justUpdatedTimerRef.current);
    };
  }, []);

  // Handle deep link to specific listing
  useEffect(() => {
    if (
      !initialLoading &&
      !loading &&
      listingIdParam &&
      listingsData.length > 0
    ) {
      // Don't use parseInt - listing IDs are UUIDs
      const listing = listingsData.find((l) => l.id === listingIdParam);

      if (listing && materialsAccordionRef.current?.openDetailPanel) {
        // Small delay to ensure accordion is ready
        deepLinkTimerRef.current = setTimeout(() => {
          materialsAccordionRef.current.openDetailPanel(listingIdParam);
          // Clear the URL param after opening
          setSearchParams({}, { replace: true });
        }, 300);
      }
    }

    return () => {
      if (deepLinkTimerRef.current) {
        clearTimeout(deepLinkTimerRef.current);
      }
    };
  }, [initialLoading, loading, listingIdParam, listingsData, setSearchParams]);

  // reset scroll button state when a new DetailedView is opened
  useEffect(() => {
    if (displayedRowId) {
      setScrollButtonUsed(false);
    }
  }, [displayedRowId]);

  // exports materials data to CSV
  const handleExport = (columnVisibility = {}) => {
    if (!filteredListingsData || filteredListingsData.length === 0) {
      setSnackbar({
        open: true,
        message: searchTerm
          ? "No matching data to export"
          : "No data to export",
        severity: "warning",
      });
      return;
    }

    try {
      const date = new Date().toISOString().split("T")[0];
      const filename = `materials-export-${date}`;

      const visibleColumns = Object.keys(columnVisibility).filter(
        (key) => columnVisibility[key] !== false
      );

      const dataToExport =
        visibleColumns.length > 0
          ? filteredListingsData.map((row) => {
              const filteredRow = {};
              visibleColumns.forEach((col) => {
                if (row.hasOwnProperty(col)) {
                  filteredRow[col] = row[col];
                }
              });
              return filteredRow;
            })
          : filteredListingsData;

      exportToCSV(dataToExport, filename);

      setSnackbar({
        open: true,
        message: "Data exported successfully",
        severity: "success",
      });
    } catch (error) {
      console.error("Error exporting data:", error);
      setSnackbar({
        open: true,
        message: "Failed to export data",
        severity: "error",
      });
    }
  };

  const handleRefreshClick = async () => {
    if (hasUnsavedChanges) {
      setShowConfirmModal(true);
    } else {
      await performRefreshInline();
    }
  };

  const handleConfirmRefresh = async () => {
    setShowConfirmModal(false);
    await performRefreshInline();
  };

  const performRefreshInline = async () => {
    setRefreshing(true);
    setJustUpdated(false);
    setHasUnsavedChanges(false);

    try {
      const success = await performRefresh();
      console.log("refresh completed, success:", success);

      if (materialsTableRef.current) {
        clearCellsTimerRef.current = setTimeout(() => {
          if (materialsTableRef.current?.clearModifiedCells) {
            materialsTableRef.current.clearModifiedCells();
          }
        }, 100);
      }
    } catch (error) {
      console.error("refresh error:", error);
    }

    refreshTimerRef.current = setTimeout(() => {
      setRefreshing(false);
      // Trigger slide-up animation on counter values
      setJustUpdated(true);
      // Clear animation class after it completes
      justUpdatedTimerRef.current = setTimeout(() => {
        setJustUpdated(false);
      }, 500);
    }, 2000);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // eslint-disable-next-line no-unused-vars
  const handleWorkspaceChangesDetected = useCallback(() => {}, []);

  // set context + navigate to workspace page
  const handleOpenWorkspace = useCallback((listing) => {
    setWorkspaceListing(listing);
    navigate("/donor/workspace");
  }, [setWorkspaceListing, navigate]);

  // set context on single-row selection so sidebar tab appears, without navigating
  const handleRowSelected = useCallback((listing) => {
    setWorkspaceListing(listing);
  }, [setWorkspaceListing]);

  const handleTableError = (error) => {
    setSnackbar({
      open: true,
      message: error.message,
      severity: "error",
    });
  };

  // card click: switch to table view and open detail panel
  const handleCardClick = useCallback((listing) => {
    setViewMode("table");
    localStorage.setItem("donate_view_mode", "table");
    setTimeout(() => {
      if (materialsAccordionRef.current?.openDetailPanel) {
        materialsAccordionRef.current.openDetailPanel(listing.id);
      }
    }, 100);
  }, []);

  const handleScrollToTop = () => {
    const findScrollingElement = () => {
      if (
        document.documentElement.scrollTop > 0 ||
        document.body.scrollTop > 0
      ) {
        return document.documentElement.scrollTop > 0
          ? document.documentElement
          : document.body;
      }

      const mainContent =
        document.querySelector("main") ||
        document.querySelector('[role="main"]') ||
        document.querySelector(".main-content");
      if (mainContent && mainContent.scrollTop > 0) {
        return mainContent;
      }

      const donateContainer = document.querySelector(".donate-container");
      if (donateContainer) {
        let parent = donateContainer.parentElement;
        while (parent && parent !== document.body) {
          if (parent.scrollTop > 0) {
            return parent;
          }
          parent = parent.parentElement;
        }
      }

      return document.documentElement;
    };

    const scrollingElement = findScrollingElement();

    if (scrollingElement.scrollTo) {
      scrollingElement.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    } else {
      scrollingElement.scrollTop = 0;
    }

    setScrollButtonUsed(true);
  };

  // classify listing status for filtering (mirrors StatusBar/ListingCard logic)
  const getListingStatus = (listing) => {
    if (listing.status?.toLowerCase() === "paused") return "paused";
    const pct = listing.quantity > 0 ? (listing.remaining_qty / listing.quantity) * 100 : 0;
    const isNew = listing.status?.toLowerCase() === "new" && pct === 100;
    if (isNew) return "new";
    if (pct === 0) return "donated";
    if (pct < 20) return "almost_out";
    if (pct < 40) return "low";
    return "available";
  };

  // extract unique materials for dropdown
  const uniqueMaterials = useMemo(
    () => [...new Set((listingsData || []).map((l) => l.material).filter(Boolean))].sort(),
    [listingsData]
  );

  // filter listings based on search term + filters + sort
  const filteredListingsData = useMemo(() => (listingsData || [])
    .filter((listing) => {
      // status filter (empty set = show all)
      if (statusFilter.size > 0) {
        if (!statusFilter.has(getListingStatus(listing))) return false;
      }

      // material filter
      if (materialFilter !== "all") {
        if (listing.material !== materialFilter) return false;
      }

      // search filter
      if (!searchTerm.trim()) return true;

      const search = searchTerm.toLowerCase();

      return (
        listing.material?.toLowerCase().includes(search) ||
        listing.item_name?.toLowerCase().includes(search) ||
        listing.quantity?.toString().includes(search) ||
        listing.remaining_qty?.toString().includes(search) ||
        listing.cost?.toString().includes(search) ||
        listing.status?.toLowerCase().includes(search) ||
        listing.delivery_instructions?.toLowerCase().includes(search) ||
        listing.date_posted?.toLowerCase().includes(search) ||
        listing.latest_pickup?.toLowerCase().includes(search)
      );
    })
    .sort((a, b) => {
      // only sort in card view
      if (viewMode !== "cards") return 0;
      switch (sortBy) {
        case "newest":
          return new Date(b.date_posted || 0) - new Date(a.date_posted || 0);
        case "oldest":
          return new Date(a.date_posted || 0) - new Date(b.date_posted || 0);
        case "pickup_asc": {
          const dA = a.latest_pickup ? new Date(a.latest_pickup) : new Date("9999-12-31");
          const dB = b.latest_pickup ? new Date(b.latest_pickup) : new Date("9999-12-31");
          return dA - dB;
        }
        case "pickup_desc": {
          const dA2 = a.latest_pickup ? new Date(a.latest_pickup) : new Date(0);
          const dB2 = b.latest_pickup ? new Date(b.latest_pickup) : new Date(0);
          return dB2 - dA2;
        }
        case "qty_desc":
          return (b.remaining_qty || 0) - (a.remaining_qty || 0);
        case "qty_asc":
          return (a.remaining_qty || 0) - (b.remaining_qty || 0);
        case "value_desc":
          return (b.cost || 0) - (a.cost || 0);
        case "value_asc":
          return (a.cost || 0) - (b.cost || 0);
        default:
          return 0;
      }
    }),
    [listingsData, statusFilter, materialFilter, searchTerm, viewMode, sortBy]
  );

  // status counts for filter chips (respects search + material, but not status itself)
  const statusCounts = useMemo(() => (listingsData || []).reduce(
    (acc, listing) => {
      // apply search filter
      if (searchTerm.trim()) {
        const search = searchTerm.toLowerCase();
        const matchesSearch =
          listing.material?.toLowerCase().includes(search) ||
          listing.item_name?.toLowerCase().includes(search) ||
          listing.quantity?.toString().includes(search) ||
          listing.remaining_qty?.toString().includes(search) ||
          listing.cost?.toString().includes(search) ||
          listing.status?.toLowerCase().includes(search);
        if (!matchesSearch) return acc;
      }
      // apply material filter
      if (materialFilter !== "all" && listing.material !== materialFilter) return acc;

      const status = getListingStatus(listing);
      acc.all++;
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    },
    { all: 0, new: 0, available: 0, low: 0, almost_out: 0, donated: 0 }
  ), [listingsData, searchTerm, materialFilter]);

  // show loading while checking permissions
  if (isChecking) {
    return (
      <LoadingSpinner
        fullPage
        size={50}
        message={t("Checking permissions...")}
      />
    );
  }

  // if no permission, the hook will redirect - but just in case, don't render
  if (!hasPermission) {
    return null;
  }

  // show loading spinner ONLY during initial load
  if (initialLoading || loading) {
    return (
      <LoadingSpinner
        fullPage
        size={50}
        message={t("Fetching list of materials...")}
      />
    );
  }

  return (
    <>
      <div className={`donate-container ${isVisible ? "visible" : ""}`}>
        {/* Page header with last updated badge */}
        <div className="page-header-row">
          <div /> {/* Spacer */}
          <LastUpdatedBadge 
            timestamp={lastUpdated} 
            onRefresh={performRefreshInline}
            isRefreshing={refreshing}
          />
        </div>

        {/* Error message banner */}
        {errorMessage && <Alert message={errorMessage} severity="error" />}

        {/* Counter cards section */}
        <div className={`counter-cards-wrapper ${refreshing ? "shimmer-overlay" : ""}`}>
          <CounterCards counters={counters} isLoading={refreshing} justUpdated={justUpdated} />
        </div>

        {/* Materials section */}
        <div className={`materials-accordion-wrapper ${refreshing ? "shimmer-overlay" : ""}`}>
          <MaterialsAccordion
            ref={materialsAccordionRef}
            materialsTableRef={materialsTableRef}
            listingsData={filteredListingsData}
            totalCount={listingsData.length}
            setSelectedListing={setSelectedListing}
            darkMode={darkMode}
            handleDataUpdate={handleDataUpdate}
            handleRefresh={handleRefreshClick}
            handleExport={handleExport}
            onTableError={handleTableError}
            isRefreshing={refreshing}
            onAddListing={handleAddListing}
            onBulkAddListings={handleBulkAddListings}
            onDeleteListings={handleDeleteListings}
            onToggleVisibility={handleToggleVisibility}
            onDetailPanelChange={setIsDetailPanelOpen}
            onDisplayedRowChange={setDisplayedRowId}
            onWorkspaceChangesDetected={handleWorkspaceChangesDetected}
            onOpenWorkspace={handleOpenWorkspace}
            onRowSelected={handleRowSelected}
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            viewMode={viewMode}
            onViewModeChange={(mode) => {
              setViewMode(mode);
              localStorage.setItem("donate_view_mode", mode);
            }}
            statusFilter={statusFilter}
            onStatusFilterChange={setStatusFilter}
            materialFilter={materialFilter}
            onMaterialFilterChange={setMaterialFilter}
            sortBy={sortBy}
            onSortByChange={setSortBy}
            statusCounts={statusCounts}
            uniqueMaterials={uniqueMaterials}
            onCardClick={handleCardClick}
          />
        </div>

        {/* Confirmation modal for discarding unsaved changes */}
        <SaveChangesModal
          open={showConfirmModal}
          onClose={() => setShowConfirmModal(false)}
          onConfirm={handleConfirmRefresh}
          darkMode={darkMode}
        />

        {/* Success/error notification snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <MuiAlert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            sx={{ width: "100%" }}
          >
            {t(snackbar.message)}
          </MuiAlert>
        </Snackbar>
      </div>

      {/* Floating Action Buttons - Bottom Right */}
      <Box
        className="floating-actions"
        sx={{
          position: "fixed",
          bottom: "75px",
          right: "20px",
          zIndex: 1000,
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          gap: "12px",
        }}
      >
        {/* scroll to Top button */}
        {isDetailPanelOpen && !scrollButtonUsed && (
          <ActionButton
            icon={faArrowUp}
            onClick={handleScrollToTop}
            variant="secondary"
            iconOnly
            tooltip={t("Scroll to Top")}
            sx={{
              borderRadius: "12px !important",
              minWidth: "44px",
              height: "44px",
              boxShadow: darkMode
                ? "0 4px 16px rgba(0, 0, 0, 0.4)"
                : "0 4px 16px rgba(0, 0, 0, 0.15)",
            }}
          />
        )}
      </Box>
    </>
  );
}

export default DonateMaterials;
